/**
 * Billing capture → Firestore update, shared by the fault and service-request
 * billing exits.
 *
 * `sendFaultToBilling` and `sendServiceRequestToBilling` are twin surfaces on
 * twin pipelines; this is the one place that decides how a
 * `BillingCaptureDetail` becomes stored fields and a timeline summary, so the
 * two can't drift on money.
 *
 * ── AUTHORITY ──────────────────────────────────────────────────────────────
 * A capture is AUTHORITATIVE: sending to billing again with a detail replaces
 * the previous itemisation outright, clearing anything the new capture didn't
 * set (via `deleteField()`), so a re-send can never leave a stale total sitting
 * next to fresh lines. Passing no detail at all is the legacy flag-only path
 * and leaves every billing field untouched.
 */
import { deleteField } from 'firebase/firestore';
import {
  computeBillingTotals,
  normalizeBillingLines,
  resolveBillingTaxMode,
  summarizeBillingLines,
  type BillingLineItem,
  type BillingTaxMode,
} from '@shared/money/billing-lines';
import { roundMoney, toFiniteMoney } from '@shared/money/round';
import { resolveTaxRate, taxWordForRate } from '@shared/money/tax';
import { getTenantTaxRateSync } from '@/utils/taxRate';
import { getCurrencySync } from '@/utils/currency';
import { getLocaleSync } from '@/utils/locale';

/** What a capture surface hands the service layer. Every field is optional. */
export interface BillingCaptureInput {
  /** Ex-tax subtotal. Recomputed from items when items are supplied. */
  billingAmount?: number;
  billingNotes?: string;
  billingItems?: BillingLineItem[];
  billingTaxMode?: BillingTaxMode;
  billingTaxRate?: number;
  billingTaxAmount?: number;
  billingTotalIncTax?: number;
}

export interface BillingCaptureUpdate {
  /** Fields to merge into the entity's Firestore update. */
  fields: Record<string, unknown>;
  /** Human one-liner for the investigation history / activity log. */
  summary: string;
  /** Ex-tax subtotal actually stored, or null when nothing was captured. */
  amountEx: number | null;
}

function formatMoney(amount: number): string {
  return amount.toLocaleString(getLocaleSync(), {
    style: 'currency',
    currency: getCurrencySync(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Turn a capture into stored fields + a timeline summary.
 *
 * The totals are RECOMPUTED here rather than trusted from the caller — the
 * modal already computed them, but a connector or a future caller might not,
 * and the stored tax must always agree with the stored lines.
 */
export function buildBillingCaptureUpdate(
  detail: BillingCaptureInput,
): BillingCaptureUpdate {
  const items = normalizeBillingLines(detail.billingItems);
  const mode = resolveBillingTaxMode(detail.billingTaxMode);
  const taxRate = resolveTaxRate(
    toFiniteMoney(detail.billingTaxRate) !== null
      ? detail.billingTaxRate
      : getTenantTaxRateSync(),
  );

  const fields: Record<string, unknown> = {};
  let amountEx: number | null = null;

  if (items.length > 0) {
    const totals = computeBillingTotals(items, taxRate, mode);
    amountEx = totals.subtotalEx;
    fields.billingItems = items;
    fields.billingAmount = totals.subtotalEx;
    fields.billingTaxMode = mode;
    fields.billingTaxRate = taxRate;
    fields.billingTaxAmount = totals.tax;
    fields.billingTotalIncTax = totals.totalIncTax;
  } else {
    // No itemisation — a flat amount (connector, or a capture confirmed with
    // no lines). Keep the amount if one was given; drop any itemisation the
    // previous capture left behind.
    const flat = toFiniteMoney(detail.billingAmount);
    fields.billingItems = deleteField();
    if (flat !== null && flat >= 0) {
      const subtotalEx = roundMoney(flat);
      const tax = mode === 'none' ? 0 : roundMoney(subtotalEx * taxRate);
      amountEx = subtotalEx;
      fields.billingAmount = subtotalEx;
      fields.billingTaxMode = mode;
      fields.billingTaxRate = taxRate;
      fields.billingTaxAmount = tax;
      fields.billingTotalIncTax = roundMoney(subtotalEx + tax);
    } else {
      fields.billingAmount = deleteField();
      fields.billingTaxMode = deleteField();
      fields.billingTaxRate = deleteField();
      fields.billingTaxAmount = deleteField();
      fields.billingTotalIncTax = deleteField();
    }
  }

  const trimmedNotes = detail.billingNotes?.trim();
  fields.billingNotes = trimmedNotes ? trimmedNotes : deleteField();

  // Timeline summary: "$363.00 inc GST (from $330.00 ex) — 1× Callout fee, 2× Labour"
  const parts: string[] = [];
  if (amountEx !== null) {
    const totalInc = toFiniteMoney(fields.billingTotalIncTax) ?? amountEx;
    parts.push(
      mode === 'none'
        ? `${formatMoney(amountEx)} (no ${taxWordForRate(taxRate)})`
        : `${formatMoney(totalInc)} inc ${taxWordForRate(taxRate)} (${formatMoney(amountEx)} ex)`,
    );
  }
  const itemSummary = summarizeBillingLines(items);
  if (itemSummary) parts.push(itemSummary);
  else if (trimmedNotes) parts.push(trimmedNotes);

  return { fields, summary: parts.join(' — '), amountEx };
}
